/* eslint-disable jsx-a11y/anchor-is-valid */
import { React, useState, useEffect, useContext } from 'react';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from '../util/getConfigValue';
import AlertModal from '../components/AlertModal';
import { useErrorBoundary } from "react-error-boundary";
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import PageTitle from '../components/PageTitle';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAnnoucements, saveAnnoucement } from "../API/announcements";
import { getSanitizedHtml } from '../util/getSanitizedHtml';
import AnouncementsCard from "../components/leaderboard/AnnouncementsCard";
import { announcmentPartition } from '../util/constants';

function AnnouncementManagementPage() {
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { state } = useLocation();
    const [alert, setAlert] = useState(state?.alert || null);
    const [appState] = useContext(AppContext);
    const { showBoundary } = useErrorBoundary();
    const { setBreadcrumbs } = useBreadcrumbs();

    const announcementIsEnabledLabel = getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.Enabled, appState)?.Value;
    const announcementIsDisabledLabel = getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.Disabled, appState)?.Value;
    const announcementAllBrandLabel = getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.AllBrands, appState)?.Value;
    const maximumCharacterCount = getConfigValue(appState?.config?.AnnouncementManagement?.Table?.MaxCharacterCount, appState)?.Value || 300;
    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState?.config?.AnnouncementManagement?.Title, appState)?.Value, path: "/announcementmanagementpage" }
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (state?.alert) {
            window.history.replaceState({}, document.title);
        }
    }, []);

    // Call for announcements
    useEffect(() => {
        getAnnouncementData();
    }, []);
  
    const getAnnouncementData = async () => {
        await getAnnoucements(announcmentPartition)
            .then((responseJson) => {
                setAnnouncements(responseJson);
                setIsLoading(false);
            })
            .catch((err) => {

            });
    }
    
    function getProduct(productId) {
        if (!productId) {
            return announcementAllBrandLabel;
        }
        
        return appState?.productDetails.find(x => x.id === productId).name;
    }

	function navigateToEdit(item) {
		navigate('/dashboardAnnouncementEditPage', { state: { announcement: item, announcements: announcements } });
	}

    function getCharacterCount(announcement) {
        const textContent = announcement?.replace(/<[^>]*>/g, '') || '';
        return textContent.length;
    }
    
    async function reorderAnnouncements(item, moveUp) {
        const index = announcements.indexOf(item);
        const newIndex = moveUp ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < announcements.length) {
            const originalAnnouncements = [...announcements];
            const newAnnouncements = [...announcements];
            [newAnnouncements[index], newAnnouncements[newIndex]] = [newAnnouncements[newIndex], newAnnouncements[index]];

            const updatedAnnouncements = newAnnouncements.map((ann, i) => ({ ...ann, order: i }));
            setAnnouncements(updatedAnnouncements);

            try {
                const savePromises = updatedAnnouncements.map(ann =>
                    saveAnnoucement(announcmentPartition, ann.id, ann.isActive, ann.content, ann.productId, ann.order)
                );
                const responses = await Promise.all(savePromises);

                if (!responses.every(res => res.ok)) {
                    setAlert({ type: 'danger', heading: getConfigValue(appState?.config?.AnnouncementManagement?.Failure?.Title, appState)?.Value, message: getConfigValue(appState?.config?.AnnouncementManagement?.FailedReordering?.Error?.Label, appState)?.Value });
                    setAnnouncements(originalAnnouncements); // revert
                }
            } catch (error) {
                showBoundary(error);
                setAnnouncements(originalAnnouncements); // revert
            }
        }
    }

    return (
        <div>
            <div className='container-xl mt-4'>
                <AnouncementsCard />
            </div>

            {alert && (
                <div className="container-xl mt-4">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <AlertModal alert={alert} />
                        </div>
                    </div>
                </div>
            )}

            <div className="container-xl mt-4">
                <div className="row justify-content-center">
                    <div className="col-12 mb-4 my-0">
                        <PageTitle title={getConfigValue(appState?.config?.AnnouncementManagement?.Title, appState)?.Value || null} />
                    </div>
                </div>


                <div className="row">
                    <div className="col-12 col-md-9 col-lg-9">
                    </div>
                    <div className="col-12 col-sm-9 col-md d-flex mt-3 mt-md-0 ps-md-0 justify-content-start justify-content-lg-end">
                        <div className='d-flex w-100'>
                            <button onClick={() => navigateToEdit(null)} className="btn btn-outline-primary btn-icon w-100 ms-2">
                                {getConfigValue(appState?.config?.AnnouncementManagement?.NewAnnouncement?.Label, appState)?.Value || null}<span className="fa-regular fa-plus btn-icon"></span>
                            </button> 
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-xl mt-5 mb-5 mb-lg-7">
                <div className="container-xl">
                    {/* <!-- Plan table header row --> */}
                    <div className="row pd-plan-table-header align-items-lg-center d-flex py-3">
                        <div className="pd-plan-col col-6 col-md-6 col-lg-6">{appState?.config?.AnnouncementManagement?.Table?.Columns?.Columns[0].Title}</div>
                        <div className="pd-plan-col col-md-2 col-lg-2 d-lg-block">{appState?.config?.AnnouncementManagement?.Table?.Columns?.Columns[1].Title}</div>
                        <div className="pd-plan-col col-md-2 col-lg-2 d-lg-block">{appState?.config?.AnnouncementManagement?.Table?.Columns?.Columns[2].Title}</div>
                        {/* <!-- Button - options --> */}
                        <div className="pd-plan-col col-3 col-md-12 col-lg-2 d-lg-block"></div>
                    </div>

                    {/* <!-- Content row --> */}
                    {isLoading ? (
                        // Loading
                        <div className="row justify-content-center mt-7">
                            <div className="col-12 col-md-6 col-lg-7 col-xl-8">
                                <div className="d-flex justify-content-center">
                                    <div className="fa-spinner fa-spin-pulse fa-regular fa-3x text-secondary">
                                        <span className="visually-hidden">{getConfigValue(appState?.config?.Common?.Loading, appState)?.Value}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : announcements.length > 0 ?
                        // Results found
                        announcements.map((announcement, index) => {
                            return (
                                <div key={announcement.id} className='row pd-plan-table-row pd-plan-table-card align-items-center py-3 py-lg-4 py-xl-5'>
                                    <div className='col-6 col-md-6 col-lg-6'>
                                        <div dangerouslySetInnerHTML={getSanitizedHtml(getCharacterCount(announcement.content) > maximumCharacterCount ? announcement.content.substring(0, maximumCharacterCount) + '...' : announcement.content)}></div>
                                    </div>
                                    <div className='col-md-2 col-lg-2 d-lg-block'>
                                        <span className='d-lg-inline'>{getProduct(announcement.productId)}</span>
                                    </div>
                                    <div className='col-md-2 col-lg-2 d-lg-block'>
                                        <span className={`fa-regular ${announcement.isActive ? 'fa-check color-success' : 'fa-x color-danger'} me-2`}></span>
                                        <span className='d-lg-inline'>{announcement.isActive ? announcementIsEnabledLabel : announcementIsDisabledLabel}</span>
                                    </div>
                                    <div className='col-3 col-md-12 col-lg-2 d-flex justify-content-center justify-content-md-end'>
                                        <button disabled={index === 0} onClick={() => reorderAnnouncements(announcement, true)} className="btn btn-ghost-secondary btn-sm btn-icon-only p-4">
                                            <span className="link-icon fa-regular fa-arrow-up"></span>
                                        </button>
                                        <button disabled={index === announcements.length - 1} onClick={() => reorderAnnouncements(announcement, false)} className="btn btn-ghost-secondary btn-sm btn-icon-only p-4">
                                            <span className="link-icon fa-regular fa-arrow-down"></span>
                                        </button>
                                        <button onClick={() => navigateToEdit(announcement)} className="btn btn-ghost-secondary btn-sm btn-icon-only p-4">
                                            <span className="link-icon fa-regular fa-pencil"></span>
                                        </button>
                                    </div>
                                </div>
                            )
                        })
                        :
                        // No results found
                        <div className="row justify-content-center mt-7">
                            <div className="col-12 col-md-6 col-lg-7 col-xl-8">
                                <div className="alert alert-warning gap-2" role="alert" style={{ flexDirection: 'column' }}>
                                    <h4 className="alert-heading">{getConfigValue(appState.config?.Common?.NoResultsFound, appState)?.Value}</h4>
                                    <p>{getConfigValue(appState.config?.Common?.AdjustFilters, appState)?.Value}</p>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}
export default AnnouncementManagementPage;
